import { Avatar, Button, Pagination, Popconfirm, Space, Table, Tooltip, notification } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import { formatedDate } from 'utils/common';
import PageTitle from '../../components/page-title';
import CustomButton from 'components/custom-button';
import AddMembersModal from 'components/add-members-modal';
import { DeleteOutlined, EditOutlined, PlusOutlined, TeamOutlined } from '@ant-design/icons';
import { SERVER_FILE_ENDPOINT } from 'constants/envs';
import { shortenName } from 'utils/short-name';
import ModalCreateEdit from './components/ModalCreatAndEdit';
import { addMembersToClan } from './api';
import styles from './styles.module.scss';

const LIMIT = 10;

const initFilter = { page: 1, limit: LIMIT };

export interface UserFilter {
  limit: number;
  page: number;
  username?: string;
  email?: string;
  startDate?: string;
  endDate?: string;
}

const clanDetailPath = (id: number) => `/group/${id}/management`;

const DomesticManger = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dataResponse, setDataResponse] = useState<any>({
    items: [],
    totalCount: 0,
  });
  const [filter, setFilter] = useState(initFilter as UserFilter);
  const [statusUpdateData, setStatusUpdateData] = useState(false);
  const [isOpenModalEditAndCreate, setIsOpenModalEditAndCreate] = useState(false);
  const [valueUserSelected, setvalueUserSelected] = useState<any>(null);
  const [deletingGroupId, setDeletingGroupId] = useState<number | null>(null);
  // Nhóm đang mở modal "Thêm thành viên".
  const [clanToAddMembers, setClanToAddMembers] = useState<any>(null);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const res: any = await AxiosUtils.get(API.groups, {
          ...filter,
        });
        setDataResponse({ items: res?.data?.data, totalCount: res?.data?.metadata?.total });
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [filter, statusUpdateData]);

  const handleDeleteGroup = async (group: any) => {
    if (deletingGroupId !== null) return;
    setDeletingGroupId(group.id);
    try {
      await AxiosUtils._delete(API.deleteDomesticGroup(group.id), undefined);
      notification.success({ message: `Đã xóa nhóm “${group.title}”` });
      // Nếu vừa xóa dòng cuối của trang hiện tại, quay về trang trước còn dữ liệu.
      if (filter.page > 1 && dataResponse.items.length === 1) {
        setFilter((prev) => ({ ...prev, page: prev.page - 1 }));
      } else {
        setStatusUpdateData((prev) => !prev);
      }
    } catch (error: any) {
      notification.error({
        message: 'Không thể xóa nhóm',
        description: error?.message || 'Có lỗi xảy ra, vui lòng thử lại.',
      });
    } finally {
      setDeletingGroupId(null);
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 60,
      ellipsis: true,
      key: 'id',
    },
    // Giữ các cột gọn để mô tả dài không kéo giãn cả bảng.
    {
      title: 'Nhóm',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      render: (value, record) => (
        <Space>
          <Avatar
            shape="square"
            size={40}
            src={record.avatar ? `${SERVER_FILE_ENDPOINT}/${record.avatar}` : undefined}
            icon={<TeamOutlined />}
          />
          <Tooltip title={value}>
            <Link to={clanDetailPath(record.id)}>{shortenName(value || '', 60) || ''}</Link>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: 'Trưởng nhóm',
      key: 'owner',
      width: 150,
      render: (_, record) => record?.owner?.nickName || record?.owner?.username || '—',
    },
    {
      title: 'Nhóm trong file TikTok',
      dataIndex: 'tiktokGroupName',
      key: 'tiktokGroupName',
      width: 200,
      render: (name: string | null) => name || 'Chưa ghép file',
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
      width: 210,
      ellipsis: { showTitle: false },
      render: (value) => (
        <Tooltip title={value || undefined}>
          <span className={styles.description}>{value || '—'}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 110,
      render: (value) => <span style={{ whiteSpace: 'nowrap' }}>{formatedDate(value)}</span>,
    },
    {
      title: 'Thao tác',
      width: 282,
      key: 'action',
      render: (_, record) => (
        <Space size={0} style={{ whiteSpace: 'nowrap' }}>
          <Button
            type="link"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => setClanToAddMembers(record)}
          >
            Thêm
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handelOpenCreateEditModal(record)}
          >
            Sửa
          </Button>
          <Button type="link" size="small" onClick={() => navigate(clanDetailPath(record.id))}>
            Chi tiết
          </Button>
          <Popconfirm
            title={`Xóa nhóm “${record.title}”?`}
            description="Thành viên sẽ rời nhóm; dữ liệu file TikTok và tài khoản vẫn được giữ."
            okText="Xóa"
            cancelText="Hủy"
            okButtonProps={{ danger: true }}
            disabled={deletingGroupId !== null}
            onConfirm={() => handleDeleteGroup(record)}
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              loading={deletingGroupId === record.id}
              disabled={deletingGroupId !== null && deletingGroupId !== record.id}
            >
              Xóa
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];
  const onChangeStatusUpdateData = () => setStatusUpdateData((prev) => !prev);

  const handleChangePage = (page: number) => {
    setFilter((preState: any) => ({ ...preState, page }));
  };

  const handelOpenCreateEditModal = (value: any) => {
    setIsOpenModalEditAndCreate(true);
    setvalueUserSelected(value);
  };

  const handleCancelCreateEditModal = () => {
    setIsOpenModalEditAndCreate(false);
    setvalueUserSelected(null);
  };

  return (
    <div>
      <PageTitle
        title="Nhóm quản lý"
        extra={
          <CustomButton
            onClick={() => handelOpenCreateEditModal(null)}
            bgColor="primary-green"
            text="Thêm nhóm"
            icon={<PlusOutlined />}
          />
        }
      />
      <div className="custom-table custom-form">
        <Table
          columns={columns}
          tableLayout="fixed"
          rowKey="id"
          pagination={false}
          dataSource={dataResponse.items}
          scroll={{ x: 1220 }}
          loading={loading}
        />
        {dataResponse.totalCount > LIMIT && (
          <Pagination
            showSizeChanger={false}
            defaultCurrent={LIMIT}
            current={filter.page}
            onChange={handleChangePage}
            showLessItems
            total={dataResponse.totalCount}
            pageSize={LIMIT}
          />
        )}
      </div>
      {isOpenModalEditAndCreate && (
        <ModalCreateEdit
          isOpen={isOpenModalEditAndCreate}
          handleCancelCreateEditModal={handleCancelCreateEditModal}
          valueUserSelected={valueUserSelected}
          onChangeStatusUpdateData={onChangeStatusUpdateData}
        />
      )}
      <AddMembersModal
        open={!!clanToAddMembers}
        title="Thêm thành viên nhóm"
        targetLabel="Nhóm quản lý"
        target={
          clanToAddMembers
            ? {
                value: clanToAddMembers.id,
                label: clanToAddMembers.title || `#${clanToAddMembers.id}`,
              }
            : undefined
        }
        userLabel="Thành viên"
        userHint="Tìm theo TikTok ID, email hoặc UID (tối thiểu 2 ký tự). Người ở nhóm khác sẽ được chuyển sang nhóm này."
        submitText="Thêm vào nhóm"
        onSubmit={(userIds, groupId) => addMembersToClan(groupId, userIds)}
        onSuccess={() => setClanToAddMembers(null)}
        onCancel={() => setClanToAddMembers(null)}
      />
    </div>
  );
};

export default DomesticManger;
